import uniqBy from 'lodash/uniqBy'
import { DateTime } from 'luxon'
import { getNetIncomeInCents } from 'pages/account/payments/transactions/transaction-helpers'
import {
  BOOKING_MIX_CATEGORIES,
  BOOKING_MIX_LABELS,
  WEEKLY_REVENUE_WEEKS,
} from './constants'

export const formatRevenueAmount = amountInDollars => {
  const rounded = Math.round(amountInDollars)
  return `$${rounded.toLocaleString()}`
}

export const formatWeekOverWeekDelta = deltaPercent => {
  const rounded = Math.round(deltaPercent)
  const prefix = rounded >= 0 ? '+' : ''
  return `${prefix}${rounded}% vs last week`
}

export const buildWeeklyRevenueSeries = (
  payments = [],
  weekCount = WEEKLY_REVENUE_WEEKS,
  referenceDate = DateTime.local(),
) => {
  const weeks = []

  for (let index = 0; index < weekCount; index += 1) {
    const offset = weekCount - index - 1
    const weekStart = referenceDate.minus({ weeks: offset }).startOf('week')
    const weekEnd = weekStart.endOf('week')

    weeks.push({
      week: `W${index + 1}`,
      weekStart,
      weekEnd,
      total: 0,
    })
  }

  payments.forEach(payment => {
    const paymentDate = DateTime.fromISO(payment.createdAt)
    if (!paymentDate.isValid) return

    const weekBucket = weeks.find(
      week =>
        paymentDate.toMillis() >= week.weekStart.toMillis() &&
        paymentDate.toMillis() <= week.weekEnd.toMillis(),
    )

    if (weekBucket) {
      weekBucket.total += getNetIncomeInCents(payment) / 100
    }
  })

  return weeks.map(({ week, total }) => ({ week, total }))
}

export const getWeeklyRevenueSummary = (series = []) => {
  if (!series.length) {
    return {
      currentWeekTotal: 0,
      weekOverWeekDeltaPercent: null,
    }
  }

  const currentWeekTotal = series[series.length - 1]?.total || 0
  const previousWeekTotal =
    series.length > 1 ? series[series.length - 2]?.total || 0 : null

  if (previousWeekTotal === null) {
    return {
      currentWeekTotal,
      weekOverWeekDeltaPercent: null,
    }
  }

  if (previousWeekTotal === 0) {
    return {
      currentWeekTotal,
      weekOverWeekDeltaPercent: currentWeekTotal > 0 ? 100 : 0,
    }
  }

  const weekOverWeekDeltaPercent =
    ((currentWeekTotal - previousWeekTotal) / previousWeekTotal) * 100

  return {
    currentWeekTotal,
    weekOverWeekDeltaPercent,
  }
}

export const buildBookingMixFromSchedule = (schedule = []) => {
  const counts = {
    [BOOKING_MIX_CATEGORIES.PRIVATE]: 0,
    [BOOKING_MIX_CATEGORIES.GROUP]: 0,
    [BOOKING_MIX_CATEGORIES.PACKAGES]: 0,
  }

  const activeBookings = uniqBy(
    schedule.filter(booking => !booking.isCanceled),
    'id',
  )

  activeBookings.forEach(booking => {
    if (booking.__typename === 'GroupEvent') {
      counts[BOOKING_MIX_CATEGORIES.GROUP] += 1
      return
    }

    if (booking.__typename === 'Session') {
      const usesLessonPack = booking.enrollments?.some(
        enrollment => enrollment.lessonCredit?.lessonPack?.id,
      )

      if (usesLessonPack) {
        counts[BOOKING_MIX_CATEGORIES.PACKAGES] += 1
      } else {
        counts[BOOKING_MIX_CATEGORIES.PRIVATE] += 1
      }
    }
  })

  const total = Object.values(counts).reduce((sum, count) => sum + count, 0)

  const segments = Object.values(BOOKING_MIX_CATEGORIES).map(category => ({
    id: category,
    label: BOOKING_MIX_LABELS[category],
    value: counts[category],
    percent: total > 0 ? Math.round((counts[category] / total) * 100) : 0,
  }))

  return {
    counts,
    total,
    segments,
  }
}

export const getWeeklyRevenueInsight = (series = []) => {
  const hasData = series.some(point => point.total > 0)
  if (!hasData) {
    return null
  }

  const totalRevenue = series.reduce(
    (sum, point) => sum + (point.total || 0),
    0,
  )
  const currentWeekTotal = series[series.length - 1]?.total || 0
  const priorWeeksTotal = series
    .slice(0, -1)
    .reduce((sum, point) => sum + (point.total || 0), 0)

  if (currentWeekTotal > 0 && priorWeeksTotal === 0) {
    return `This week brought in ${formatRevenueAmount(
      currentWeekTotal,
    )} — your first revenue in the last 8 weeks.`
  }

  const { weekOverWeekDeltaPercent } = getWeeklyRevenueSummary(series)

  if (weekOverWeekDeltaPercent !== null && weekOverWeekDeltaPercent >= 50) {
    return `Revenue grew ${Math.round(
      weekOverWeekDeltaPercent,
    )}% week over week — ${formatRevenueAmount(currentWeekTotal)} this week.`
  }

  if (weekOverWeekDeltaPercent !== null && weekOverWeekDeltaPercent <= -50) {
    return `Revenue dipped ${Math.abs(
      Math.round(weekOverWeekDeltaPercent),
    )}% week over week.`
  }

  if (totalRevenue > 0 && currentWeekTotal > 0) {
    const currentShare = Math.round((currentWeekTotal / totalRevenue) * 100)
    if (currentShare >= 50) {
      return `This week accounts for ${currentShare}% of your ${formatRevenueAmount(
        totalRevenue,
      )} earned over the last 8 weeks.`
    }
  }

  return `You've earned ${formatRevenueAmount(
    totalRevenue,
  )} across the last 8 weeks.`
}

export const getBookingMixInsight = ({ segments = [], total = 0 } = {}) => {
  if (!total) {
    return 'No bookings yet this month. Share your booking link to start filling your schedule.'
  }

  const leader = [...segments].sort(
    (left, right) => right.value - left.value,
  )[0]

  if (!leader?.value) {
    return null
  }

  return `${leader.label} lessons account for ${leader.percent}% of bookings this month.`
}
